import React from 'react';
import { Paper, Stack, Typography, Skeleton, Box, ButtonBase, Grid, Tooltip } from '@mui/material';
import {
    SUMMARY_CARD_PAPER_SX,
    SummaryMetricPairSkeleton,
    SummaryValueSkeleton,
} from './ExplorerLoadingSkeletons';
import { explorerAccentAt } from './utils';

/** Value color comes from the card's position (see `EXPLORER_ACCENT_SEQUENCE`). */
export interface SummaryCardConfig {
    title: string;
    value: string | number | React.ReactNode;
    icon?: React.ReactNode;
    onClick?: () => void;
    /** Hover tooltip for the full card. */
    tooltip?: string;
    /** When set, this card shows a skeleton even if the section is not loading. */
    isLoading?: boolean;
}

interface SummaryCardProps extends SummaryCardConfig {
    /** Accent for the value; omit for default text color. */
    color?: string;
    isLoading?: boolean;
}

/** Icons inherit the title color instead of the value accent. */
const SummaryCardIcon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box component="span" sx={{ display: 'inline-flex', color: 'text.primary' }}>
        {children}
    </Box>
);

function wrapSummaryCardTooltip(
    node: React.ReactElement,
    tooltip?: string,
): React.ReactElement {
    if (!tooltip) return node;
    return (
        <Tooltip title={tooltip} arrow placement="top">
            <Box component="span" sx={{ display: 'block', width: '100%' }}>
                {node}
            </Box>
        </Tooltip>
    );
}

export const SummaryCard: React.FC<SummaryCardProps> = ({
    title,
    value,
    icon,
    color,
    onClick,
    tooltip,
    isLoading = false,
}) => {
    if (isLoading) {
        const showHeader = Boolean(title);
        const pairLayout =
            value != null && typeof value !== 'string' && typeof value !== 'number';
        return (
            <Paper sx={SUMMARY_CARD_PAPER_SX}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                    sx={{ mb: 1.5, flexShrink: 0 }}
                >
                    {showHeader ? (
                        <>
                            {icon ? <SummaryCardIcon>{icon}</SummaryCardIcon> : null}
                            <Typography
                                variant="body2"
                                fontWeight={600}
                                color="text.primary"
                                sx={{ lineHeight: 1.25 }}
                            >
                                {title}
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Skeleton
                                variant="circular"
                                animation="wave"
                                width={24}
                                height={24}
                                sx={{ flexShrink: 0 }}
                            />
                            <Skeleton
                                variant="rounded"
                                animation="wave"
                                width={100}
                                height={16}
                                sx={{ borderRadius: 0.5 }}
                            />
                        </>
                    )}
                </Stack>
                {pairLayout ? <SummaryMetricPairSkeleton /> : <SummaryValueSkeleton />}
            </Paper>
        );
    }

    const content = (
        <>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
                {icon ? <SummaryCardIcon>{icon}</SummaryCardIcon> : null}
                <Typography
                    variant="body2"
                    fontWeight={600}
                    color="text.primary"
                    sx={{ lineHeight: 1.25, textWrap: 'balance' }}
                >
                    {title}
                </Typography>
            </Stack>
            {typeof value === "string" || typeof value === "number" ? (
                <Typography
                    variant="h3"
                    fontWeight={700}
                    color={color ? undefined : "text.primary"}
                    sx={{
                        ...(color ? { color } : null),
                        fontFamily: (theme) => theme.typography.fontFamily,
                    }}
                >
                    {value}
                </Typography>
            ) : (
                <Box
                    sx={{
                        color: color || "text.primary",
                        width: "100%",
                    }}
                >
                    {value}
                </Box>
            )}
        </>
    );

    if (onClick) {
        return wrapSummaryCardTooltip(
            <ButtonBase
                onClick={onClick}
                sx={{
                    width: '100%',
                    borderRadius: 1,
                    display: 'block',
                    textAlign: 'center',
                    '&:hover .MuiPaper-root': { bgcolor: 'action.hover' },
                }}
            >
                <Paper
                    sx={{
                        ...SUMMARY_CARD_PAPER_SX,
                        height: '100%',
                        transition: 'background-color 0.15s',
                    }}
                >
                    {content}
                </Paper>
            </ButtonBase>,
            tooltip,
        );
    }

    return wrapSummaryCardTooltip(
        <Paper sx={SUMMARY_CARD_PAPER_SX}>{content}</Paper>,
        tooltip,
    );
};

interface SummarySectionProps {
    /** Omit or leave empty to hide the section heading. */
    title?: string;
    cards: SummaryCardConfig[];
    isLoading?: boolean;
    /** Defaults to h2; use a smaller variant for nested sections. */
    titleVariant?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    /** When set, every card value uses this color instead of the positional accents. */
    valueColor?: string;
}

/** N equal columns on md+ so all summary cards sit in one row. */
function getSummaryGridLayout(cardCount: number): {
    containerColumns?: { xs: number; sm: number; md: number; lg: number };
    itemSize: number | { xs: number; sm: number; md: number; lg: number };
} {
    // 5+ cards: custom container columns so each card is 1 unit and stays one row on lg.
    if (cardCount >= 5) {
        const mdCols = cardCount <= 6 ? cardCount : Math.ceil(cardCount / 2);
        return {
            containerColumns: {
                xs: 1,
                sm: Math.min(cardCount, 2),
                md: mdCols,
                lg: cardCount,
            },
            itemSize: 1,
        };
    }
    const lgSpan = cardCount === 3 ? 4 : cardCount === 4 ? 3 : 12;
    const smSpan = cardCount >= 3 ? 4 : 6;
    return {
        itemSize: { xs: 12, sm: smSpan, md: lgSpan, lg: lgSpan },
    };
}

export const SummarySection: React.FC<SummarySectionProps> = ({
    title,
    cards,
    isLoading = false,
    titleVariant = 'h2',
    valueColor,
}) => {
    const { containerColumns, itemSize } = getSummaryGridLayout(cards.length);

    return (
        <Box sx={{ mb: 2.5 }}>
            {title ? (
                <Typography variant={titleVariant} sx={{ mb: 2 }}>
                    {title}
                </Typography>
            ) : null}
            <Grid container spacing={2} columns={containerColumns}>
                {isLoading ? (
                    cards.map((card, index) => (
                        <Grid key={index} size={itemSize} sx={{ minWidth: 0 }}>
                            <SummaryCard
                                title={card.title}
                                value={card.value}
                                icon={card.icon}
                                color=""
                                isLoading={true}
                            />
                        </Grid>
                    ))
                ) : (
                    cards.map((card, index) => (
                        <Grid key={index} size={itemSize} sx={{ minWidth: 0 }}>
                            <SummaryCard
                                {...card}
                                color={valueColor ?? explorerAccentAt(index)}
                                isLoading={card.isLoading ?? false}
                            />
                        </Grid>
                    ))
                )}
            </Grid>
        </Box>
    );
};

export default SummarySection;

